<template>
  <CustomDrawer
    v-model="drawerVisible"
    :title="formTitle"
    @close="handleClose"
    width="600px"
    direction="right"
    :z-index="2100"
  >
    <div v-if="loading || (!formSchema && !loadError)" class="flex justify-center items-center py-20">
      <CustomLoader :size="32" />
    </div>

    <div v-else-if="formSchema" id="form_drawer_content">
      <div class="form_fields">
        <div v-for="field in formFields" :key="field.id" class="mb-4">
          <div
            v-if="field.type === fieldTypes.SECTION"
            class="mb-6"
            v-show="isSectionVisible(field.id)"
          >
            <div
              class="flex items-center gap-2 mb-3 cursor-pointer"
              @click="toggleSection(field.id)"
            >
              <ActionWrapper
                source="form_center_panel"
                :title="sectionStates[field.id] ? 'Expand' : 'Collapse'"
              >
                <ThemeImage
                  :icon="imageNames.DOWN_ARROW_WHITE"
                  alt="toggle_icon"
                  :class="[
                    'size-4 transition-transform duration-200',
                    sectionStates[field.id] ? '-rotate-90' : 'rotate-0',
                  ]"
                />
              </ActionWrapper>

              <span class="text-sm font-semibold">{{ field.label }}</span>
            </div>

            <div v-show="sectionStates[field.id] !== false" class="ml-6">
              <div v-if="field.children && field.children.length > 0">
                <div
                  v-for="childField in field.children"
                  :key="childField.id"
                  class="mb-3"
                >
                  <FormField
                    v-show="isFieldVisible(childField.id)"
                    :ref="
                      (el) => {
                        if (el) fieldRefs[childField.id] = el;
                      }
                    "
                    :field="enrichFieldWithRules(childField)"
                    v-model="formData[childField.id]"
                    :disabled="isFieldReadonly(childField.id)"
                    :error="!!fieldErrors[childField.id]"
                    :errorMessage="fieldErrors[childField.id]"
                    :onEnterKey="() => handleEnterKey(childField.id)"
                    :onValueChange="() => clearFieldError(childField.id)"
                    :entityType="props.entityType"
                    :entityId="props.entityId"
                    @entity-record-selected="handleEntityRecordSelected"
                  />
                </div>
              </div>
            </div>
          </div>

          <FormField
            v-else
            v-show="isFieldVisible(field.id)"
            :ref="
              (el) => {
                if (el) fieldRefs[field.id] = el;
              }
            "
            :field="enrichFieldWithRules(field)"
            v-model="formData[field.id]"
            :disabled="isFieldReadonly(field.id)"
            :error="!!fieldErrors[field.id]"
            :errorMessage="fieldErrors[field.id]"
            :onEnterKey="() => handleEnterKey(field.id)"
            :onValueChange="() => clearFieldError(field.id)"
            :entityType="props.entityType"
            :entityId="props.entityId"
            @entity-record-selected="handleEntityRecordSelected"
          />
        </div>
      </div>
    </div>

    <div v-else class="text-center py-20 secondary_color">
      <span class="text-sm">Unable to load form</span>
    </div>

    <template #footer>
      <div class="flex flex-row gap-2 justify-end">
        <a-button class="secondary_medium" @click="handleClose">
          Cancel
        </a-button>
        <a-button
          class="primary_medium"
          @click="handleSubmit"
          :loading="submitting"
          :disabled="!hasFormData"
        >
          {{
            submitting
              ? isEditMode
                ? "Updating..."
                : "Submitting..."
              : isEditMode
                ? "Update"
                : "Submit"
          }}
        </a-button>
      </div>
    </template>
  </CustomDrawer>
</template>

<script setup>
import { ref, watch, computed, nextTick } from "vue";
import { message } from "ant-design-vue";
import { useFormRules } from "@/src/composables/forms/useFormRules.js";
import { getCurrentUserId } from "@/src/utils/conditionalAccessUtils.js";
import { useFormValidation } from "@/src/composables/forms/useFormValidation.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import {
  fieldTypes,
  fieldTypeGroups,
  normalizePhoneValue,
} from "@/src/constants/formConstants.js";
import dayjs from "dayjs";
import ThemeImage from "@/src/components/ThemeImage.vue";
import CustomDrawer from "@/src/components/CustomDrawer.vue";
import FormField from "@/src/components/form_components/fields/FormField.vue";
import ActionWrapper from "@/src/components/custom_components/ActionWrapper.vue";
import FormService from "@/src/api/FormService.js";
import CustomLoader from "@/src/components/CustomLoader.vue";
import store from "@/src/store/index.js";
import {
  fetchPipelineOptions,
  fetchAllPipelineStageOptions,
  fetchPipelineStageOptionsByPipelineId,
} from "@/src/utils/dynamicFormUtils.js";
import { forceSelectFormFields } from "@/src/constants/crmConstants.js";

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  formId: {
    type: [String, Number],
    default: null,
  },
  formVersion: {
    type: Object,
    default: null,
  },
  entityType: {
    type: String,
    default: null,
  },
  entityId: {
    type: [String, Number],
    default: null,
  },
  isBlocking: {
    type: Boolean,
    default: false,
  },
  isEditMode: {
    type: Boolean,
    default: false,
  },
  submissionId: {
    type: [String, Number],
    default: null,
  },
  initialValues: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(["close", "submit", "submit-error"]);

const drawerVisible = computed({
  get: () => props.visible,
  set: (value) => {
    if (!value) {
      emit("close");
    }
  },
});

const loading = ref(false);
const loadError = ref(false);
const submitting = ref(false);
const formSchema = ref(null);
const formRules = ref(null);
const formData = ref({});
const initialFormData = ref({});
const formInitialized = ref(false);
const sectionStates = ref({});
const fieldErrors = ref({});
const fieldRefs = {};

const formTitle = computed(() => {
  if (!formSchema.value) return "Form";

  if (typeof formSchema.value === "object" && formSchema.value.name) {
    return formSchema.value.name;
  }

  return "Form";
});

const isEmpty = (val) => {
  return val === null || val === undefined || val === "";
};

const hasFormData = computed(() => {
  if (!formInitialized.value) {
    return false;
  }

  // For edit mode, check if any field has changed from initial value
  if (props.isEditMode) {
    for (const key of Object.keys(formData.value)) {
      const currentVal = formData.value[key];
      const initialVal = initialFormData.value[key];

      if (isEmpty(currentVal) && isEmpty(initialVal)) {
        continue;
      }

      if (dayjs.isDayjs(currentVal) || dayjs.isDayjs(initialVal)) {
        if (!dayjs.isDayjs(currentVal) || !dayjs.isDayjs(initialVal)) {
          return true;
        }
        if (!currentVal.isSame(initialVal, "minute")) {
          return true;
        }
      } else if (Array.isArray(currentVal)) {
        if (!Array.isArray(initialVal)) {
          return true;
        }
        if (JSON.stringify(currentVal) !== JSON.stringify(initialVal)) {
          return true;
        }
      } else if (currentVal !== initialVal) {
        return true;
      }
    }
    return false;
  }

  // If no visible field is mandatory, allow submit without requiring data
  const hasAnyMandatoryField = Object.keys(fieldRequired.value).some(
    (fieldId) =>
      fieldRequired.value[fieldId] === true &&
      fieldVisibility.value[fieldId] !== false,
  );

  if (!hasAnyMandatoryField) {
    return true;
  }

  // For create mode, check if any field has user-entered data
  // (excluding auto-filled currentUserSelected and currentDateSelected fields)
  const findField = (fields, fieldId) => {
    for (const field of fields) {
      if (field.id === fieldId) return field;
      if (field.type === fieldTypes.SECTION && field.children) {
        const found = findField(field.children, fieldId);
        if (found) return found;
      }
    }
    return null;
  };

  for (const key of Object.keys(formData.value)) {
    const val = formData.value[key];
    const field = findField(formFields.value, key);

    // Skip fields with currentUserSelected or currentDateSelected (auto-filled defaults)
    if (field?.currentUserSelected || field?.currentDateSelected) {
      continue;
    }

    if (dayjs.isDayjs(val)) {
      return true;
    } else if (Array.isArray(val) && val.length > 0) {
      return true;
    } else if (val !== null && val !== undefined && val !== "") {
      return true;
    }
  }

  return false;
});

const formFields = computed(() => {
  if (!formSchema.value) return [];

  if (Array.isArray(formSchema.value)) {
    return formSchema.value;
  }

  if (formSchema.value.fields && Array.isArray(formSchema.value.fields)) {
    return formSchema.value.fields;
  }

  return [];
});

const {
  initializeFieldStates,
  executeRules,
  isFieldVisible,
  isFieldReadonly,
  isFieldRequired,
  fieldVisibility,
  fieldRequired,
} = useFormRules(formRules, formData, formFields);

const { validateForm: validateFormNew } = useFormValidation();

watch(
  () => props.formVersion,
  (newVersion, oldVersion) => {
    if (newVersion) {
      loadFormVersion(newVersion);
    } else if (oldVersion && !newVersion) {
      // formVersion became null (new form being fetched), reset to show loader
      formSchema.value = null;
      formRules.value = null;
    }
  },
  { immediate: true },
);

watch(
  () => props.visible,
  (newVal) => {
    if (newVal && !formSchema.value && props.formVersion) {
      loadFormVersion(props.formVersion);
    }
    if (newVal) {
      initializeSectionStates();
      fieldErrors.value = {};
    }
  },
);

const loadFormVersion = async (version) => {
  if (loading.value) {
    return;
  }

  try {
    loading.value = true;
    loadError.value = false;

    if (version.schema_json) {
      formSchema.value = version.schema_json;
    }

    if (version.rules_json) {
      formRules.value = version.rules_json;
    }

    initializeFormData();
    initializeFieldStates();

    await loadPipelineFieldOptions();
    await fetchAndApplyPrefillData();

    executeRules("on_load", formData.value);

    await nextTick();

    // Store initial form data for edit mode change detection
    if (props.isEditMode) {
      initialFormData.value = {};
      Object.keys(formData.value).forEach((key) => {
        const val = formData.value[key];
        if (dayjs.isDayjs(val)) {
          initialFormData.value[key] = val;
        } else if (Array.isArray(val)) {
          initialFormData.value[key] = [...val];
        } else {
          initialFormData.value[key] = val;
        }
      });
    }

    formInitialized.value = true;
  } catch (error) {
    console.error("Error loading form version:", error);
    message.error("Failed to load form");
    loadError.value = true;
  } finally {
    loading.value = false;
  }
};

const findFieldById = (fields, fieldId) => {
  for (const field of fields) {
    if (field.id === fieldId) return field;
    if (field.type === fieldTypes.SECTION && field.children) {
      const found = findFieldById(field.children, fieldId);
      if (found) return found;
    }
  }
  return null;
};

const findFieldByFieldName = (fields, fieldName) => {
  for (const field of fields) {
    if (field.fieldName === fieldName) return field;
    if (field.type === fieldTypes.SECTION && field.children) {
      const found = findFieldByFieldName(field.children, fieldName);
      if (found) return found;
    }
  }
  return null;
};

const loadPipelineFieldOptions = async () => {
  const pipelineField = findFieldByFieldName(formFields.value, "pipeline_id");
  const stageField = findFieldByFieldName(formFields.value, "pipeline_stage_id");

  if (!pipelineField && !stageField) return;

  if (pipelineField) {
    pipelineField.options = await fetchPipelineOptions();
  }
  if (stageField) {
    stageField.options = await fetchAllPipelineStageOptions();
  }
};

// Cascade: when pipeline_id changes, filter pipeline_stage_id options
const getPipelineFieldId = () => {
  const field = findFieldByFieldName(formFields.value, "pipeline_id");
  return field?.id || null;
};

watch(
  () => {
    const fieldId = getPipelineFieldId();
    return fieldId ? formData.value[fieldId] : null;
  },
  async (newPipelineId) => {
    const stageField = findFieldByFieldName(formFields.value, "pipeline_stage_id");
    if (!stageField) return;

    if (newPipelineId) {
      stageField.options = await fetchPipelineStageOptionsByPipelineId(newPipelineId);
    } else {
      stageField.options = await fetchAllPipelineStageOptions();
    }

    // Clear stage value if it doesn't belong to the new pipeline
    if (stageField.id && formData.value[stageField.id]) {
      const validStageIds = (stageField.options || []).map((o) => o.value);
      if (!validStageIds.includes(formData.value[stageField.id])) {
        formData.value[stageField.id] = null;
      }
    }
  },
);

// For pipeline/stage fields, resolve a prefilled slug/name to the actual option ID
const resolveOptionValue = (value, field) => {
  if (
    !value ||
    !field?.options?.length ||
    !forceSelectFormFields.includes(field.fieldName)
  ) {
    return value;
  }

  // If the value already matches an option value (ID), use it as-is
  const matchById = field.options.find((o) => o.value === value);
  if (matchById) return value;

  // Otherwise, try matching by label (slug/name) and return the corresponding ID
  const matchByLabel = field.options.find(
    (o) =>
      o.label === value ||
      o.label?.toLowerCase() === String(value).toLowerCase(),
  );
  return matchByLabel ? matchByLabel.value : value;
};

const normalizePrefillValue = (value, field) => {
  if (value === null || value === undefined) {
    return value;
  }

  if (field?.type === fieldTypes.PHONE) {
    return normalizePhoneValue(value);
  }

  if (fieldTypeGroups.DATE_TIME_FIELDS.includes(field?.type) && value) {
    return dayjs(value);
  }

  if (field?.type === fieldTypes.EMAIL && Array.isArray(value)) {
    const primaryEmail = value.find((e) => e.is_primary) || value[0];
    return primaryEmail?.email || "";
  }

  if (Array.isArray(value) && value.length > 0 && typeof value[0] === "object") {
    const firstItem = value[0];
    return firstItem?.value || firstItem?.name || firstItem?.id || "";
  }

  return value;
};

const fetchAndApplyPrefillData = async () => {
  if (props.isEditMode) {
    return;
  }
  if (!props.formId || !props.entityType || !props.entityId) {
    return;
  }

  try {
    const response = await FormService.forms.fetchPrefillData(
      props.formId,
      props.entityType,
      props.entityId,
    );

    if (response?.success && response?.prefill_data) {
      Object.entries(response.prefill_data).forEach(([fieldId, value]) => {
        const field = findFieldById(formFields.value, fieldId);

        if (
          field &&
          field.type === fieldTypes.USER &&
          field.currentUserSelected &&
          !value
        ) {
          return;
        }

        if (
          field &&
          fieldTypeGroups.DATE_TIME_FIELDS.includes(field.type) &&
          field.currentDateSelected &&
          !value
        ) {
          return;
        }

        let normalized = normalizePrefillValue(value, field);
        normalized = resolveOptionValue(normalized, field);
        formData.value[fieldId] = normalized;
      });
    }
  } catch (error) {
    console.error("Error fetching prefill data:", error);
  }
};

const initializeFormData = () => {
  formData.value = {};

  const initializeFields = (fields) => {
    fields.forEach((field) => {
      if (field.type === fieldTypes.SECTION && field.children) {
        initializeFields(field.children);
      } else if (
        field.type !== fieldTypes.TEXT_DISPLAY &&
        field.type !== fieldTypes.IMAGE
      ) {
        const prefillValue = props.initialValues?.[field.id] ?? props.initialValues?.[field.fieldName];
        if (prefillValue !== undefined) {
          const value = prefillValue;
          if (fieldTypeGroups.DATE_TIME_FIELDS.includes(field.type) && value) {
            formData.value[field.id] = dayjs(value);
          } else if (field.type === fieldTypes.PHONE) {
            formData.value[field.id] = normalizePhoneValue(value);
          } else {
            formData.value[field.id] = resolveOptionValue(value, field);
          }
        } else if (field.type === fieldTypes.MULTI_SELECT) {
          formData.value[field.id] = [];
        } else if (field.type === fieldTypes.SINGLE_SELECT) {
          formData.value[field.id] = undefined;
        } else if (
          field.type === fieldTypes.USER &&
          field.currentUserSelected
        ) {
          formData.value[field.id] = getCurrentUserId();
        } else if (field.type === fieldTypes.USER) {
          formData.value[field.id] = undefined;
        } else if (
          fieldTypeGroups.DATE_TIME_FIELDS.includes(field.type) &&
          field.currentDateSelected
        ) {
          formData.value[field.id] = dayjs();
        } else if (field.type === fieldTypes.ENTITY) {
          formData.value[field.id] = undefined;
        } else {
          formData.value[field.id] = "";
        }
      }
    });
  };

  if (formFields.value && formFields.value.length > 0) {
    initializeFields(formFields.value);
  }
};

const handleEntityRecordSelected = ({ objectSlug, recordData }) => {
  if (!recordData) return;

  const applyToFields = (fields) => {
    fields.forEach((field) => {
      if (field.type === fieldTypes.SECTION && field.children) {
        applyToFields(field.children);
        return;
      }
      if (field.isProperty && field.attributeData?.object_slug === objectSlug) {
        const attrSlug = field.attributeData?.slug || field.fieldName;
        const rawValue =
          recordData.attributes?.[attrSlug]?.value ?? recordData[attrSlug];
        if (rawValue !== undefined) {
          formData.value[field.id] = normalizePrefillValue(rawValue, field);
        }
      }
    });
  };

  applyToFields(formFields.value);
};

const initializeSectionStates = () => {
  sectionStates.value = {};
  formFields.value.forEach((field) => {
    if (field.type === fieldTypes.SECTION) {
      sectionStates.value[field.id] = !field.collapsed;
    }
  });
};

const toggleSection = (sectionId) => {
  sectionStates.value[sectionId] = !sectionStates.value[sectionId];
};

const getTextBasedFieldIds = () => {
  const textFieldTypes = [
    fieldTypes.TEXT,
    fieldTypes.EMAIL,
    fieldTypes.NUMBER,
    fieldTypes.URL,
    fieldTypes.DOMAIN,
    fieldTypes.PHONE,
  ];

  const textFieldIds = [];

  const collectFieldIds = (fields) => {
    fields.forEach((field) => {
      if (field.type === fieldTypes.SECTION && field.children) {
        collectFieldIds(field.children);
      } else if (textFieldTypes.includes(field.type)) {
        textFieldIds.push(field.id);
      }
    });
  };

  if (formFields.value && formFields.value.length > 0) {
    collectFieldIds(formFields.value);
  }

  return textFieldIds;
};

const clearFieldError = (fieldId) => {
  if (fieldErrors.value[fieldId]) {
    const updatedErrors = { ...fieldErrors.value };
    delete updatedErrors[fieldId];
    fieldErrors.value = updatedErrors;
  }
};

const handleEnterKey = (currentFieldId) => {
  const textFieldIds = getTextBasedFieldIds();
  const currentIndex = textFieldIds.indexOf(currentFieldId);

  if (currentIndex === -1) return;

  if (currentIndex === textFieldIds.length - 1) {
    handleSubmit();
  } else {
    const nextFieldId = textFieldIds[currentIndex + 1];
    if (fieldRefs[nextFieldId]) {
      setTimeout(() => {
        fieldRefs[nextFieldId].focus();
      }, 100);
    }
  }
};

const handleSubmit = async () => {
  try {
    const validationResult = validateFormNew(
      formFields.value,
      formData.value,
      fieldVisibility.value,
      fieldRequired.value,
    );

    if (!validationResult.isValid) {
      fieldErrors.value = validationResult.errors;

      const firstErrorFieldId = validationResult.fieldOrder.find(
        (fieldId) => validationResult.errors[fieldId],
      );

      if (firstErrorFieldId && fieldRefs[firstErrorFieldId]) {
        setTimeout(() => {
          const fieldElement = fieldRefs[firstErrorFieldId].$el;
          if (fieldElement) {
            fieldElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
            setTimeout(() => {
              fieldRefs[firstErrorFieldId].focus();
            }, 300);
          }
        }, 100);
      }

      return;
    }

    fieldErrors.value = {};
    submitting.value = true;

    emit("submit", {
      formId: props.formId,
      entityType: props.entityType,
      entityId: props.entityId,
      values: formData.value,
      isEdit: props.isEditMode,
      submissionId: props.submissionId,
    });
  } catch (error) {
    console.error("Error submitting form:", error);
    message.error("Failed to submit form");
    submitting.value = false;
  }
};

const enrichFieldWithRules = (field) => {
  const enriched = {
    ...field,
    required: field.required || isFieldRequired(field.id),
  };
  if (field.type === fieldTypes.MEETING_LOG) {
    enriched.entityType = props.entityType;
    enriched.entityId = props.entityId;
  }
  return enriched;
};

const isSectionVisible = (sectionId) => {
  return isFieldVisible(sectionId);
};

const resetSubmitting = () => {
  submitting.value = false;
};

const handleClose = () => {
  formData.value = {};
  initialFormData.value = {};
  formInitialized.value = false;
  submitting.value = false;
  loadError.value = false;
  emit("close");
};

defineExpose({
  resetSubmitting,
});
</script>

<style scoped>
.form_fields {
  max-height: calc(100vh - (200px + var(--banner-height)));
  overflow-y: auto;
}
</style>
